import * as React from 'react';
import type { Control, FieldValues, Validate } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import Help from '@mui/icons-material/Help';
import { FormControl } from '@mui/material';
import { Input as MuiInput } from '@mui/material';

import { Container, ContainerItem, Error, InputLabel, Tooltip } from 'ui/atoms';
import { getInputSx } from 'ui/atoms/Input/input.atom';
import { errorMessages } from 'util/forms.util';

interface ValidationRule<T> {
  message: string;
  value: T;
}

export interface InputCurrencyProps {
  label: string;
  name: string;
  control?: Control;
  dataCy?: string;
  defaultValue?: string;
  onBlur?: () => void;
  onChange?: (e?: number | null) => void;
  readOnly?: boolean;
  viewOnly?: boolean;
  rules?: {
    required?: ValidationRule<boolean>;
    maxLength?: ValidationRule<number>;
    minLength?: ValidationRule<number>;
    max?: ValidationRule<number>;
    min?: ValidationRule<number>;
    pattern?: ValidationRule<RegExp>;
    validate?:
      | Validate<string | number, FieldValues>
      | Record<string, Validate<string | number, FieldValues>>;
  };
  tooltip?: string;
}

const StyledMuiInput = React.forwardRef((props: any, ref) => {
  return <MuiInput {...props} ref={ref} sx={getInputSx(props, 'auto', props.color)} disableUnderline />;
});

export const InputCurrency: React.FC<InputCurrencyProps> = (props) => {
  return (
    <FormControl fullWidth data-cy={props.dataCy}>
      <Container padding={0} align="flex-start">
        <ContainerItem padding={0} align="center" flex margin="0 0 6px 0">
          <InputLabel
            dataCy={'input-label'}
            label={props.label}
            name={props.name}
            required={props.viewOnly ? false : props.rules?.required?.value}
            readOnly={props.readOnly}
            rules={props.rules}
            viewOnly={props.viewOnly}
          />
        </ContainerItem>

        {props.tooltip ? (
          <ContainerItem padding={0} align="center" flex margin="-3px 0 0 0">
            <Tooltip variant="text" title={props.tooltip} placement="right">
              <Help color="primary" />
            </Tooltip>
          </ContainerItem>
        ) : null}
      </Container>
      <Controller
        control={props.control}
        name={props.name}
        defaultValue={props.defaultValue}
        rules={props.rules}
        render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => (
          <>
            <NumericFormat
              id={props.name}
              // we need to set the value to respond to programmatic updates such as form.setValue
              // and convert from cents back to dollars for display
              value={typeof value === 'number' ? value / 100 : null}
              className={'MuiInput-input MuiInputBase-input'}
              onBlur={() => (props?.onBlur ? props.onBlur() : onBlur())}
              onValueChange={({ floatValue }) => {
                const finalValue = !!floatValue || floatValue === 0 ? Math.round(floatValue * 100) : null;

                if (props.onChange) {
                  props.onChange(finalValue);
                }
                onChange(finalValue);
              }}
              thousandSeparator
              prefix={'$'}
              decimalScale={2}
              fixedDecimalScale
              disabled={props.readOnly || props.viewOnly}
              placeholder={'$0.00'}
              readOnly={props.readOnly}
              viewOnly={props.viewOnly}
              name={props.name}
              error={error}
              customInput={StyledMuiInput}
            />

            <Error
              content={
                props.viewOnly
                  ? ''
                  : error
                  ? `* ${props.label} ${error.message || errorMessages()[error.type]}`
                  : ''
              }
              dataCy="validation-message"
            />
          </>
        )}
      />
    </FormControl>
  );
};
